// Iter-206e — Audit "Factures manquées" : missions où une facture aurait
// dû être générée mais ne l'a pas été à cause de la règle stricte
// all-CRA-approved. Liste, filtre overdue, génération en bulk.
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Loader2, AlertTriangle, RefreshCw, Zap, Calendar, FileText, ChevronDown, ChevronUp,
} from "lucide-react";

const fmt = (v, ccy) => `${Number(v || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2 })} ${ccy || ""}`.trim();

export default function MissedInvoicesAudit() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [monthsBack, setMonthsBack] = useState(6);
  const [running, setRunning] = useState(null); // single mid-y-m key while running
  const [bulkRunning, setBulkRunning] = useState(false);
  const [expanded, setExpanded] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/billing/audit/missed-invoices?months_back=${monthsBack}`);
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec du chargement");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [monthsBack]);

  const runOne = async (it) => {
    const k = `${it.mission_id}|${it.year}|${it.month}`;
    setRunning(k);
    try {
      const r = await api.post(`/billing/audit/missed-invoices/${it.mission_id}/${it.year}/${it.month}/run`);
      const st = r.data?.result?.status;
      if (["DONE", "SHADOW"].includes(st)) {
        toast.success(`Facture générée — statut : ${st}`);
      } else {
        toast.warning(`Statut : ${st}${r.data?.result?.reason ? " — " + r.data.result.reason : ""}`);
      }
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec"); }
    finally { setRunning(null); }
  };

  const runAll = async () => {
    if (!window.confirm(`Générer et envoyer ${data?.total || 0} facture(s) ? Les envois en délai dépassé partiront immédiatement.`)) return;
    setBulkRunning(true);
    try {
      const r = await api.post(`/billing/audit/missed-invoices/run-all?months_back=${monthsBack}`);
      const ok = (r.data?.results || []).filter((x) => ["DONE", "SHADOW"].includes(x.status)).length;
      toast.success(`${ok}/${r.data?.processed || 0} factures générées avec succès`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec"); }
    finally { setBulkRunning(false); }
  };

  return (
    <div className="p-4 md:p-8 lg:p-12 max-w-[1400px] space-y-6" data-testid="missed-invoices-audit-page">
      <div>
        <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono">FINANCE</div>
        <h1 className="font-heading font-bold text-3xl text-zinc-900">Audit — Factures manquées</h1>
        <p className="text-sm text-zinc-600 max-w-3xl mt-1">
          Missions disposant d'un bon de commande, dont les CRA des rôles couverts par PO sont approuvés, mais pour lesquelles aucune facture n'a été émise.
          Les CRA des rôles <em>non couverts par PO</em> n'entrent pas dans le périmètre — ils seront facturés lorsqu'un PO les couvrira.
        </p>
      </div>

      <div className="flex items-center gap-3 flex-wrap" data-testid="missed-invoices-controls">
        <label className="text-xs text-zinc-600">
          Profondeur :
          <select
            value={monthsBack}
            onChange={(e) => setMonthsBack(Number(e.target.value))}
            className="ml-2 border border-zinc-200 rounded px-2 py-1 text-xs"
            data-testid="months-back-select"
          >
            {[1, 3, 6, 12].map((n) => (
              <option key={n} value={n}>{n} mois</option>
            ))}
          </select>
        </label>
        <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="refresh-btn">
          {loading ? <Loader2 size={12} className="animate-spin mr-1.5" /> : <RefreshCw size={12} className="mr-1.5" />}
          Rafraîchir
        </Button>
        {data?.total > 0 && (
          <Button onClick={runAll} disabled={bulkRunning} className="bg-zinc-950 text-white hover:bg-zinc-800" data-testid="run-all-btn">
            {bulkRunning ? <Loader2 size={12} className="animate-spin mr-1.5" /> : <Zap size={12} className="mr-1.5" />}
            Générer toutes ({data.total})
          </Button>
        )}
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <SummaryCard label="Total détecté" value={data?.total ?? "—"} testId="total-count" />
        <SummaryCard label="En retard d'envoi" value={data?.overdue_count ?? "—"} accent="text-rose-700" testId="overdue-count" />
        <SummaryCard label="Période analysée" value={data?.scanned_periods?.length ? `${data.scanned_periods.length} mois` : "—"} testId="scanned-periods" />
      </div>

      {loading ? (
        <div className="py-10 text-center text-sm text-zinc-500">
          <Loader2 className="inline animate-spin mr-2" size={14} /> Analyse en cours…
        </div>
      ) : !data || data.total === 0 ? (
        <div className="rounded-md border border-emerald-200 bg-emerald-50 px-4 py-6 text-sm text-emerald-900 flex items-start gap-2">
          <FileText size={14} className="mt-0.5 shrink-0" />
          <div>
            <strong>Aucune facture manquée détectée.</strong> Toutes les missions avec PO qui pouvaient être facturées sur les {monthsBack} derniers mois ont une facture émise (ou une raison explicite — pause/annulation).
          </div>
        </div>
      ) : (
        <div className="border border-zinc-200 rounded-lg overflow-hidden bg-white">
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[920px]">
              <thead className="bg-zinc-50 text-[10px] uppercase tracking-wider text-zinc-500 font-mono">
                <tr>
                  <th className="text-left px-4 py-2">Mission · Client</th>
                  <th className="text-left px-4 py-2">Période</th>
                  <th className="text-left px-4 py-2">Date d'envoi prévue</th>
                  <th className="text-right px-4 py-2">Total TTC</th>
                  <th className="text-left px-4 py-2">Périmètre PO</th>
                  <th className="text-right px-4 py-2">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {data.items.map((it) => {
                  const k = `${it.mission_id}|${it.year}|${it.month}`;
                  const isExpanded = !!expanded[k];
                  return (
                    <React.Fragment key={k}>
                      <tr className="hover:bg-zinc-50/50" data-testid={`missed-row-${k}`}>
                        <td className="px-4 py-3">
                          <div className="font-medium">{it.mission_title}</div>
                          <div className="text-[10px] font-mono text-zinc-500">{it.client_name}</div>
                        </td>
                        <td className="px-4 py-3 text-xs font-mono">{it.year}-{String(it.month).padStart(2, "0")}</td>
                        <td className="px-4 py-3 text-xs">
                          {it.dispatch_date}
                          {it.overdue && (
                            <span className="ml-2 text-[9px] font-mono uppercase px-1.5 py-0.5 rounded bg-rose-100 text-rose-900 border border-rose-200">
                              En retard
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right font-mono">{fmt(it.total_ttc, it.currency)}</td>
                        <td className="px-4 py-3 text-xs">
                          {it.po_covered_roles?.length ? (
                            <button
                              type="button"
                              className="inline-flex items-center gap-1 text-zinc-700 hover:underline"
                              onClick={() => setExpanded({ ...expanded, [k]: !isExpanded })}
                            >
                              {it.po_covered_roles.length} rôle(s) couvert(s) {isExpanded ? <ChevronUp size={10} /> : <ChevronDown size={10} />}
                            </button>
                          ) : "—"}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <Button
                            size="sm"
                            onClick={() => runOne(it)}
                            disabled={running === k || bulkRunning}
                            className="bg-zinc-950 text-white hover:bg-zinc-800"
                            data-testid={`run-${k}`}
                          >
                            {running === k ? <Loader2 size={12} className="animate-spin mr-1" /> : <Zap size={12} className="mr-1" />}
                            Générer
                          </Button>
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr className="bg-zinc-50">
                          <td colSpan={6} className="px-4 py-3 text-xs space-y-2">
                            <div>
                              <span className="font-mono uppercase text-[10px] text-zinc-500">Rôles couverts par PO : </span>
                              <span className="text-zinc-700">{it.po_covered_roles.join(", ") || "—"}</span>
                            </div>
                            {it.missing_outside_po?.length > 0 && (
                              <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-2 py-1.5 text-amber-900">
                                <AlertTriangle size={12} className="mt-0.5 shrink-0" />
                                <div>
                                  <strong>{it.missing_outside_po.length} CRA en attente hors PO</strong> (non bloquants — leurs jours seront facturés une fois un PO les couvrant uploadé) :
                                  <ul className="list-disc list-inside mt-0.5">
                                    {it.missing_outside_po.map((u) => (
                                      <li key={u.user_id}>{u.name} <span className="font-mono text-[10px]">[{u.status}]</span></li>
                                    ))}
                                  </ul>
                                </div>
                              </div>
                            )}
                            <div className="text-zinc-500 italic flex items-center gap-1">
                              <Calendar size={11} /> Mode d'envoi : {it.send_mode === "IMMEDIATE" ? "Immédiat" : "Fin de mois"}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function SummaryCard({ label, value, accent = "text-zinc-900", testId }) {
  return (
    <div className="border border-zinc-200 rounded-md bg-white px-4 py-3" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono">{label}</div>
      <div className={`text-2xl font-bold mt-1 ${accent}`}>{value}</div>
    </div>
  );
}
